import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ThemeContext } from "./contexts/ThemeContext";
import { NavbarContext } from "./contexts/NavbarContext";
import { ABOUT, CONTACT, PROFILE, REALISATIONS, SERVICES } from "../config/routes";
import "./styles/About.css";

import florianImagePng from "../images/png/background/florian_background.png";
import florianImageAvif from "../images/avif/background/florian_background.avif";
import florianImageWebp from "../images/webp/background/florian_background.webp";

const skillGroups = [
  {
    title: "Frontend",
    items: ["React", "Vite", "JavaScript", "TypeScript selon projets", "Interfaces métiers"],
  },
  {
    title: "Backend",
    items: ["Node.js", "API", "logique métier", "authentification", "maintenance"],
  },
  {
    title: "Mobile",
    items: ["Flutter", "React Native", "Firebase", "Supabase", "notifications"],
  },
  {
    title: "Bases de données",
    items: ["MySQL", "PostgreSQL", "SQL propriétaire", "modélisation", "données métier"],
  },
  {
    title: "Intégrations",
    items: ["Stripe", "PayPal", "OAuth", "Gmail API", "FCM", "API IA"],
  },
  {
    title: "Déploiement",
    items: ["Nginx", "PM2", "SSL", "VPS", "configuration production"],
  },
  {
    title: "SEO / visibilité",
    items: ["SEO technique", "sitemap", "metadata", "Open Graph", "structure de pages"],
  },
];

const projectBenefits = [
  "Comprendre rapidement le contexte métier avant de coder.",
  "Cadrer une première version réaliste et maintenable.",
  "Choisir une architecture pragmatique plutôt qu’une usine à gaz.",
  "Relier frontend, backend, base de données, API et déploiement.",
  "Garder une communication claire sur les limites, les risques et les choix.",
];

const journeyBlocks = [
  {
    eyebrow: "Logiciel métier / ERP",
    title: "Rigueur acquise sur des systèmes existants",
    period: "3 ans d’alternance",
    text:
      "Une expérience dans un environnement ERP et logiciel métier, avec des systèmes déjà en production, de la maintenance et des contraintes réelles.",
    items: [
      "C, PL/I et SQL propriétaire",
      "Maintenance et évolution de l’existant",
      "Logique métier et systèmes complexes",
      "Rigueur liée aux contraintes de production",
    ],
  },
  {
    eyebrow: "Freelance / Dreamer Agency",
    title: "Construction de produits digitaux complets",
    period: "2 à 3 ans en indépendant",
    text:
      "Une activité orientée conception et développement de produits concrets : applications web, SaaS, dashboards, mobile, API et déploiement.",
    items: [
      "React, Node.js, Vite, JavaScript / TypeScript selon projets",
      "MySQL, PostgreSQL, Firebase, Supabase",
      "Stripe, PayPal, OAuth, Gmail API, FCM",
      "Applications mobiles, automatisations, SEO et relation client",
    ],
  },
];

function SectionIntro({ eyebrow, title, children }) {
  return (
    <div className="about8-sectionIntro">
      <p className="about8-eyebrow">{eyebrow}</p>
      <h2>{title}</h2>
      {children ? <p>{children}</p> : null}
    </div>
  );
}

export default function AboutV8() {
  const { darkMode } = useContext(ThemeContext);
  const { navbarActive, setNavbarActive, scrollPosition } = useContext(NavbarContext);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 599 : false
  );

  const siteOrigin = "https://dreameragency.fr";
  const canonicalUrl = `${siteOrigin}${ABOUT}`;
  const metaTitle = "À propos - Dreamer Agency / Florian Da Cunha";
  const metaDesc =
    "Découvrez Dreamer Agency, l’activité indépendante de Florian Da Cunha, développeur full-stack à Toulouse spécialisé en applications web, SaaS, mobiles et solutions métiers.";
  const ogImage = `${siteOrigin}/media/OG/florian_background.png`;

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "Person",
        name: "Florian Da Cunha",
        jobTitle: "Développeur full-stack React / Node.js / TypeScript",
        url: `${siteOrigin}${PROFILE}`,
        address: {
          "@type": "PostalAddress",
          addressLocality: "Toulouse",
          addressCountry: "FR",
        },
        knowsAbout: [
          "React",
          "Node.js",
          "TypeScript",
          "SaaS",
          "Applications mobiles",
          "Solutions métiers",
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ProfessionalService",
        name: "Dreamer Agency",
        url: siteOrigin,
        founder: {
          "@type": "Person",
          name: "Florian Da Cunha",
        },
        areaServed: "France",
        serviceType: "Développement d’applications web, SaaS, mobiles et solutions métiers",
      },
    ],
    [siteOrigin]
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 599);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const preventTouchMove = (e) => e.preventDefault();

    if (navbarActive && isMobile) {
      document.body.addEventListener("touchmove", preventTouchMove, { passive: false });
    } else {
      document.body.removeEventListener("touchmove", preventTouchMove);
    }

    return () => document.body.removeEventListener("touchmove", preventTouchMove);
  }, [navbarActive, isMobile]);

  useEffect(() => {
    const mainContainer = document.querySelector(".about8");
    if (!mainContainer) return undefined;

    if (navbarActive) {
      mainContainer.classList.add("menu-open");
      document.body.classList.add("menu-open");
    } else {
      mainContainer.classList.remove("menu-open");
      document.body.classList.remove("menu-open");
    }

    return () => {
      mainContainer.classList.remove("menu-open");
      document.body.classList.remove("menu-open");
    };
  }, [navbarActive]);

  useEffect(() => {
    if (!navbarActive) window.scrollTo(0, scrollPosition);
  }, [navbarActive, scrollPosition]);

  const handleMainContainerClick = () => {
    if (navbarActive && isMobile) setNavbarActive(false);
  };

  return (
    <main className={`about8 ${darkMode ? "dark" : "light"}`} onClick={handleMainContainerClick}>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDesc} />
        <meta name="twitter:image" content={ogImage} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <section className="about8-hero">
        <div className="about8-container about8-heroGrid">
          <div className="about8-heroContent">
            <p className="about8-eyebrow">À propos</p>
            <h1>À propos de Dreamer Agency</h1>
            <p className="about8-lead">
              Dreamer Agency est l’activité indépendante de Florian Da Cunha, développeur full-stack basé à Toulouse,
              dédiée à la conception d’applications web, SaaS, mobiles et solutions métiers.
            </p>
            <div className="about8-heroFacts" aria-label="Repères de parcours">
              <span>Bac+5 informatique</span>
              <span>Environ 5 ans d’expérience projet</span>
              <span>ERP, web, SaaS et mobile</span>
            </div>
            <div className="about8-actions">
              <Link to={REALISATIONS} className="about8-btn about8-btnPrimary">
                Voir les réalisations
              </Link>
              <Link to={PROFILE} className="about8-btn about8-btnSecondary">
                Profil développeur
              </Link>
            </div>
          </div>

          <picture className="about8-portrait">
            <source srcSet={florianImageAvif} type="image/avif" />
            <source srcSet={florianImageWebp} type="image/webp" />
            <img src={florianImagePng} alt="Florian Da Cunha" />
          </picture>
        </div>
      </section>

      <section className="about8-section">
        <div className="about8-container about8-split">
          <SectionIntro eyebrow="Approche" title="Une approche portée par un développeur full-stack">
            Dreamer Agency n’est pas une grosse agence impersonnelle. C’est une activité portée par Florian, avec une
            attention particulière aux usages, à la maintenabilité et aux contraintes réelles du projet.
          </SectionIntro>

          <div className="about8-textPanel">
            <p>
              L’objectif est de concevoir des solutions concrètes, adaptées au besoin métier, puis de les construire
              proprement : interface, backend, base de données, API, intégrations, déploiement et maintenance.
            </p>
            <p>
              Cette position permet de passer du cadrage produit à l’exécution technique sans multiplier les
              interlocuteurs ni perdre la logique métier en route.
            </p>
          </div>
        </div>
      </section>

      <section className="about8-section about8-sectionAlt">
        <div className="about8-container">
          <SectionIntro eyebrow="Parcours" title="Un parcours entre logiciel métier et produits web">
            Florian combine un socle issu du logiciel métier avec une pratique freelance orientée produits digitaux
            complets.
          </SectionIntro>

          <div className="about8-journeyGrid">
            {journeyBlocks.map((block) => (
              <article className="about8-journeyCard" key={block.title}>
                <p className="about8-eyebrow">{block.eyebrow}</p>
                <h3>{block.title}</h3>
                <p className="about8-period">{block.period}</p>
                <p>{block.text}</p>
                <ul>
                  {block.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="about8-section">
        <div className="about8-container about8-benefitsLayout">
          <SectionIntro eyebrow="Ce que cela change" title="Des choix techniques reliés au besoin métier">
            Le bénéfice principal n’est pas une promesse abstraite : c’est une façon plus lisible de transformer une
            idée, une contrainte ou un process en produit utilisable.
          </SectionIntro>

          <div className="about8-benefitList">
            {projectBenefits.map((benefit) => (
              <article className="about8-benefit" key={benefit}>
                <p>{benefit}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="about8-section about8-sectionAlt">
        <div className="about8-container">
          <SectionIntro eyebrow="Compétences principales" title="Une synthèse des sujets couverts">
            Cette page donne une vue d’ensemble. Le détail recruteur complet reste disponible sur le profil
            développeur.
          </SectionIntro>

          <div className="about8-skillsGrid">
            {skillGroups.map((group) => (
              <article className="about8-skillCard" key={group.title}>
                <h3>{group.title}</h3>
                <div className="about8-chipList">
                  {group.items.map((item) => (
                    <span key={`${group.title}-${item}`}>{item}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="about8-section">
        <div className="about8-container about8-usesGrid">
          <article className="about8-useCard">
            <p className="about8-eyebrow">Clients</p>
            <h2>Découvrir les services et les réalisations</h2>
            <p>
              Le site présente les types de solutions que Dreamer Agency peut construire : web, SaaS, mobile,
              dashboards, automatisations et intégrations API.
            </p>
            <Link to={SERVICES} className="about8-btn about8-btnSecondary">
              Découvrir les services
            </Link>
          </article>

          <article className="about8-useCard">
            <p className="about8-eyebrow">Recruteurs</p>
            <h2>Consulter le profil développeur</h2>
            <p>
              Le site sert aussi de portfolio technique pour présenter les projets livrés et le parcours de Florian.
            </p>
            <Link to={PROFILE} className="about8-btn about8-btnSecondary">
              Voir le profil développeur
            </Link>
          </article>
        </div>
      </section>

      <section className="about8-finalCta">
        <div className="about8-container about8-finalInner">
          <div>
            <p className="about8-eyebrow">Contact</p>
            <h2>Un projet web, SaaS, mobile ou une opportunité full-stack à discuter ?</h2>
            <p>
              Le plus simple est d’envoyer un message avec le contexte. On peut ensuite décider si un échange plus
              cadré est utile.
            </p>
          </div>
          <div className="about8-actions about8-actionsEnd">
            <Link to={CONTACT} className="about8-btn about8-btnPrimary">
              Me contacter
            </Link>
            <Link to={REALISATIONS} className="about8-btn about8-btnSecondary">
              Voir les réalisations
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
